<template>
  <el-card>
    <div
      v-if="showHeader"
      slot="header"
      class="clearfix"
      >
      <span>{{ getTransText(name, 'label', base) }}</span>
    </div>
    <el-input
      class="filter-input"
      :placeholder="getTransText(name, 'filter', base)"
      v-model="filterText"
      >
    </el-input>
    <el-tree
      v-show="isShowTree"
      class="filter-tree"
      :data="data"
      :props="defaultProps"
      :default-expanded-keys="expandKeys"
      :expand-on-click-node="false"
      :show-checkbox="showCheckbox"
      node-key="path"
      highlight-current
      :filter-node-method="filterNode"
      @check-change="check"
      ref="tree"
      >
      <div
        class="custom-tree-node"
        slot-scope="{ node, data }"
        >
        <el-tooltip
          :open-delay="500"
          >
          <div slot="content">
            {{ data.path }}
          </div>
          <span>{{ node.label }}</span>
        </el-tooltip>
        <span
          v-if="data.isBase && isEditable"
          >
          <el-button
            type="text"
            size="mini"
            @click="() => setChecked(node, isNodeBasic)"
            >
            {{ $t('api.actions.perm.basic') }}
          </el-button>
          <el-button
            type="text"
            size="mini"
            @click="() => setChecked(node, isNodeView)"
            >
            {{ $t('api.actions.perm.readOnly') }}
          </el-button>
        </span>
      </div>
    </el-tree>
  </el-card>
</template>

<script>

import { defFetchList } from '@/api/default'
import { getPermMap, getRefPath, isExPath } from '@/utils/perm'
import { hasOneShowingChildren } from '@/utils/tree'
import { getTransText, generateTitle } from '@/utils/i18n'
import { constantRouterMap } from '@/router'

export default {
  name: 'perm-tree',
  props: {
    base: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: 'perm'
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    isExpandRootOnly: {
      type: Boolean,
      default: false
    },
    isInitShowTree: {
      type: Boolean,
      default: true
    },
    isEditable: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      nChecked: 0,
      showCheckbox: true,
      filterText: '',
      defaultProps: {
        children: 'children',
        label: function (node) {
          return node.label || node.path
        }
      },
      rules: [{
        required: true,
        validator: this.validArray,
        trigger: 'blur'
      }],
      isShowTree: true,
      data: [],
      permMap: {},
      apiPerm: {},
      refPath: {},
      expandKeys: ['/']
    }
  },
  created () {
  },
  mounted () {
    this.isShowTree = this.isInitShowTree
    this.init()
  },
  computed: {
    rootLabel () {
      return this.$t('api.actions.perm.root')
    },
    othersLabel () {
      return this.$t('api.actions.perm.others')
    }
  },
  methods: {
    hasOneShowingChildren,
    getTransText,
    generateTitle,
    setShowTree (v) {
      this.isShowTree = v
    },
    resetTree () {
      let root = this.$refs.tree.getNode('/')
      this.resetNode(root)
    },
    resetNode (node) {
      if (node) {
        if (node.data) {
          node.data.disabled = true
        }
        if (!node.checked && !node.indeterminate) {
          node.remove()
          return
        }
        if (node.childNodes) {
          for (let i = node.childNodes.length - 1; i >= 0; i--) {
            this.resetNode(node.childNodes[i])
          }
        }
      }
    },
    isNodeBasic (node) {
      return node.data.action === 'view' ||
        node.data.action === 'edit' ||
        node.data.action === 'add' ||
        node.data.action === 'send'
    },
    isNodeView (node) {
      return node.data.action === 'view'
    },
    setChecked (node, check) {
      if (node.data.isAction) {
        node.setChecked(check(node), true)
        return
      }
      if (node.childNodes) {
        node.childNodes.forEach(c => {
          this.setChecked(c, check)
        })
      }
    },
    initNode (e, parent) {
      let l = '-'
      let path = e.path
      if (this.hasOneShowingChildren(e.children) &&
        !e.children[0].children &&
        !e.alwaysShow) {
        e = e.children[0]
      }
      if (e.meta && e.meta.title) {
        l = this.generateTitle(e.meta.title)
      }
      let keyword = l
      if (parent) {
        keyword = parent.keyword + '.' + keyword
      }
      let node = {
        label: l,
        keyword: keyword,
        path: path,
        name: e.name,
        isBase: true,
        children: []
      }
      if (e.children) {
        e.children.forEach(c => {
          if (c.hidden) {
            return
          }
          // Prevent duplicated path
          if (node.path === c.path) {
            node.path += '.'
          }
          node.children.push(this.initNode(c, node))
        })
        if (!this.isExpandRootOnly) {
          this.expandKeys.push(node.path)
        }
      } else {
        this.addNodePerm(node)
      }
      return node
    },
    addNodePerm (node) {
      if (this.apiPerm[node.path]) {
        this.apiPerm[node.path].forEach(act => {
          this.addActNode(node, act)
        })
      }
    },
    addActNode (node, act) {
      let p = node.path + '/' + act
      if (this.apiPerm[p]) {
        let label = this.$t('api.actions.perm.' + act)
        let keyword = node.keyword + '.' + label
        let children = []
        if (this.apiPerm[p]) {
          this.apiPerm[p].forEach(e => {
            children.push({
              data: this.permMap[e.path],
              path: e.path,
              keyword: keyword + ' ' + e.path
            })
          })
        }
        node.children.push({
          label: label,
          keyword: keyword,
          path: p,
          isAction: true,
          action: act,
          children: children,
          refPath: this.refPath[p]
        })
      }
    },
    setTreeChecked (perm) {
      this.$refs.tree.setChecked(perm, true, true)
    },
    initChecked (permData, pType) {
      permData.forEach(p => {
        this.permMap[p.perm] = p
      })
      this.init()
      this.$nextTick(() => {
        permData.forEach(p => {
          if (p.policy_type === pType) {
            this.setTreeChecked(p.perm)
          }
        })
        this.nChecked = this.$refs.tree.getCheckedNodes(true).length
      })
    },
    init () {
      this.apiPerm = getPermMap()
      this.refPath = getRefPath()
      this.data = [{
        'path': '/',
        'label': this.rootLabel,
        'isBase': true,
        'children': []
      }]
      constantRouterMap.forEach(e => {
        if (e.hidden) {
          return
        }
        this.data[0].children.push(this.initNode(e))
      })
      let others = {
        'path': '/others',
        'label': this.othersLabel,
        'isBase': true,
        'children': [
          {
            'label': this.$t('route.group_member.name'),
            'path': '/user/allmember',
            'isBase': true,
            'children': []
          },
          {
            'label': this.$t('api.branch.delivery'),
            'path': '/user/dlvaddress',
            'isBase': true,
            'children': []
          },
          {
            'label': this.$t('api.contact.name'),
            'path': '/merchant/contact',
            'isBase': true,
            'children': []
          },
          {
            'label': this.$t('api.optFields.authType.map.partner'),
            'path': '/merchant/partner',
            'isBase': true,
            'children': []
          }
        ]
      }
      others.children.forEach(c => {
        this.addNodePerm(c)
      })
      this.data[0].children.push(others)
    },
    checkPathExists () {
      defFetchList('/casbin/routes').then(res => {
        let n = 0
        res.data.forEach(e => {
          let key = e.method + ' ' + e.path
          if (!isExPath(key)) {
            let node = this.$refs.tree.getNode(key)
            if (!node) {
              console.log('[DEBUG] Warn: path missing permission: ' + key)
              n++
            }
          }
        })
        if (n === 0) {
          console.log('[DEBUG] OK: all path exists')
        }
      }).catch(err => {
        console.log(err)
      })
    },
    uncheck () {
      this.$refs.tree.setChecked('/', false, true)
    },
    check (node, st, isChild) {
      if (node.refPath && st) {
        node.refPath.forEach(path => {
          this.$refs.tree.setChecked(path, true, true)
        })
      }
      if (node.children) {
        node.children.forEach(c => {
          this.check(c, st, true)
        })
      }
      if (!isChild) {
        this.nChecked = this.$refs.tree.getCheckedNodes(true).length
      }
    },
    filterNode (value, data) {
      if (!value) {
        return true
      }
      let flag = false
      if (data.keyword) {
        flag = flag || data.keyword.indexOf(value) !== -1
      }
      return flag
    },
    getChecked () {
      return this.$refs.tree.getCheckedNodes(true)
    },
    getAllChecked () {
      return this.$refs.tree.getCheckedNodes(false, true)
    },
    getPermData (curPerm, policyType) {
      let perm = []
      let nodeMap = {}
      let nodes = this.getChecked()
      nodes.forEach(node => {
        if (nodeMap[node.path]) {
          return
        }
        if (node.data) {
          perm.push(node.data)
        } else {
          perm.push({
            perm: node.path,
            policy_type: policyType
          })
        }
        nodeMap[node.path] = true
      })
      if (curPerm) {
        curPerm.forEach(p => {
          if (!nodeMap[p.perm] && p.policy_type === policyType) {
            perm.push({
              id: p.id,
              perm: p.perm,
              policy_type: p.policyType,
              is_for_delete: true
            })
          }
        })
      }
      return perm
    }
  },
  watch: {
    filterText (val) {
      this.$refs.tree.filter(val)
    }
  }
}
</script>
